import { X } from 'lucide-react'
import { StatusBadge } from './status-badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'

interface OrderItem { name: string; quantity: number; price: number }

interface OrderDetail {
  id: string
  customer_name: string
  customer_email: string
  app_name: string
  status: 'pending' | 'processing' | 'shipping' | 'completed' | 'refunded'
  /** Empty when the source row carries no line items. Never a placeholder row. */
  items: OrderItem[]
  /** Optional: only present when the source actually reports the figure. */
  subtotal?: number
  shipping?: number
  discount?: number
  total: number
  currency: string
  created_at: string
}

interface OrderDetailDrawerProps {
  order: OrderDetail | null
  onClose: () => void
  onAction?: (action: string, orderId: string) => void
  className?: string
}

export function OrderDetailDrawer({ order, onClose, onAction, className }: OrderDetailDrawerProps) {
  const { strings } = useStudioLocale()
  const copy = strings.businessScreens.activity.drawer
  if (!order) return null
  const symbol = order.currency === 'KRW' ? '₩' : '$'

  return (
    <>
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
    <div className={cn('fixed inset-y-0 right-0 w-[420px] border-l border-border bg-background shadow-xl z-50 overflow-y-auto', className)}>
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 className="text-lg font-semibold">{copy.title(order.id)}</h2>
        <button onClick={onClose} aria-label={copy.close} className="rounded-lg p-1.5 hover:bg-accent"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <StatusBadge status={order.status} size="md" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onAction?.('process', order.id)}>{copy.process}</Button>
            <Button size="sm" variant="outline" onClick={() => onAction?.('refund', order.id)}>{copy.refund}</Button>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm"><span className="text-muted-foreground">{copy.customer}</span> {order.customer_name}</p>
          <p className="text-sm"><span className="text-muted-foreground">{copy.email}</span> {order.customer_email}</p>
          <p className="text-sm"><span className="text-muted-foreground">{copy.app}</span> {order.app_name}</p>
          <p className="text-sm"><span className="text-muted-foreground">{copy.date}</span> {order.created_at}</p>
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">{copy.items}</h3>
          {order.items.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="order-detail-no-items">
              {copy.noItems}
            </p>
          ) : order.items.map((item, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span>{item.name} × {item.quantity}</span>
              <span>{symbol}{item.price.toLocaleString()}</span>
            </div>
          ))}
          <div className="border-t border-border pt-2 mt-2 space-y-1">
            {order.subtotal !== undefined && <div className="flex justify-between text-sm"><span className="text-muted-foreground">{copy.subtotal}</span><span>{symbol}{order.subtotal.toLocaleString()}</span></div>}
            {order.shipping !== undefined && <div className="flex justify-between text-sm"><span className="text-muted-foreground">{copy.shipping}</span><span>{symbol}{order.shipping.toLocaleString()}</span></div>}
            {order.discount !== undefined && order.discount > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">{copy.discount}</span><span className="text-red-500">-{symbol}{order.discount.toLocaleString()}</span></div>}
            <div className="flex justify-between text-sm font-semibold border-t border-border pt-1"><span>{copy.total}</span><span>{symbol}{order.total.toLocaleString()}</span></div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export type { OrderDetail, OrderItem }
